'use client';
import { useEffect, FC } from 'react';
import { Facebook, Link, Share } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { shareToFacebook, initFacebookSdk } from '../lib/facebook';
import { logAnalyticsEvent } from '@/lib/analytics';

interface ShareButtonsProps {
  quote: string;
  author: string;
  quoteId: string;
}

const ShareButtons: FC<ShareButtonsProps> = ({ quote, author, quoteId }) => {
  const shareText = `"${quote}" - ${author}`;

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  const shareUrl = `${siteUrl}/quote/${quoteId}`;

  useEffect(() => {
    initFacebookSdk();

  }, []);


  const shareToFB = () => {
    shareToFacebook(shareUrl);
    logAnalyticsEvent('facebook_share');
  };

  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank');
    logAnalyticsEvent('x_share');
  };

  const shareToApp = async () => {
    logAnalyticsEvent('app_share');

    const shareData = {
      url: shareUrl,
      title: `Quote by ${author}`,
    };
    if (typeof navigator.share === 'function' &&
      navigator.canShare &&
      navigator.canShare(shareData)) {
      await navigator.share(shareData);
    }
    else {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: 'Copied to clipboard',
      });
    }

  };


  return (
    <div className="mt-8">
      <div className="flex items-center justify-center gap-2 mb-4">
        <Share size={18} className="text-gray-600" />
        <span className="text-sm font-medium text-gray-600">Share this quote</span>
      </div>
      <div className="flex space-x-4 justify-center">
        <Button
          variant="outline"
          size="icon"
          className="bg-gradient-to-br from-blue-600 to-blue-700 hover:opacity-90 text-white rounded-full w-12 h-12 shadow-md transition-transform hover:scale-110 duration-300 hover:text-white"
          onClick={shareToFB}
          title="Share to Facebook"
        >
          <Facebook size={20} />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="bg-gradient-to-br from-gray-800 to-black hover:opacity-90 text-white rounded-full w-12 h-12 shadow-md transition-transform hover:scale-110 duration-300 hover:text-white"
          onClick={shareToTwitter}
          title="Share to X"
        >
          <FontAwesomeIcon icon={faXTwitter} className="fa-fw" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="bg-gradient-to-br from-blue-400 to-blue-500 hover:opacity-90 text-white rounded-full w-12 h-12 shadow-md transition-transform hover:scale-110 duration-300 hover:text-white"
          onClick={async () => await shareToApp()}
          title="Share to App"
        >
          <Link size={20} />
        </Button>
      </div>
    </div>
  );
};

export default ShareButtons;
